/**
 * New Deal Page
 *
 * Page showing the deal creation flow
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useOrg } from '@/contexts/OrgContext';
import { useDealOperations } from '@/hooks/deals/useDealOperations';
import { DealStage } from '@equaliq/api-model';
import { PATH_ROOT } from '@/routes/paths';
import { DEMO_CONTRACT_IDS } from '@/mockData/demoContractData';
import { DealCreationFlow } from './components/DealCreationFlow';
import { createFileApi } from '@/api/file/CreateFileApi';
import { putFileToPresignedUrl } from '@/api/file/putFileToPresignedUrl';
import { pushDealThroughEngineApi } from '@/api/deal-engine/DealEngine';

export const NewDealPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Route is authoritative for which surface we're on — NOT the global
  // useIsDemo() localStorage flag, which is set by the welcome-page toggle and
  // PERSISTS after a demo tour. A user who toured the demo then opened the REAL
  // /new-deal route would otherwise hit the demo short-circuit below and be
  // routed to a fake contract. The demo page lives at /demo/new-deal (wrapped
  // in DemoWrapper); the real page is /new-deal. The /demo path prefix is the
  // unambiguous, weakest-assumption signal — direct links work too.
  const isDemo = location.pathname.startsWith('/demo');
  const { selectedOrgId } = useOrg();
  const { createDeal } = useDealOperations(selectedOrgId);
  const [isCreating, setIsCreating] = useState(false);

  const initialMethodParam = searchParams.get('method');
  const initialMethod =
    initialMethodParam === 'upload' ||
    initialMethodParam === 'template' ||
    initialMethodParam === 'meeting'
      ? initialMethodParam
      : null;
  const fromContractIq = searchParams.get('from') === 'contract-iq';
  const initialTemplateId = searchParams.get('templateId');

  const handleComplete = async (dealData: any) => {
    if (isDemo) {
      navigate(`${PATH_ROOT.demo.analysis}/${DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL}`);
      return;
    }

    if (!selectedOrgId || isCreating) return;
    setIsCreating(true);

    try {
      const title = dealData.context?.trim() ? dealData.context.slice(0, 100) : 'Untitled Deal';
      const coreFiles: File[] = Array.isArray(dealData.coreFiles) ? dealData.coreFiles : [];

      // Upload path: the user attached a contract PDF. Drive the SAME engine
      // seam the email ingest uses — (1) mint a presigned S3 URL via
      // /files/create, (2) PUT the bytes, (3) push the file through the
      // engine with its sourceFileId. The deal + 8-stage analysis are created
      // by the manager workflow, identical to orchestrate-ingest. The first
      // core file is the analysis source (mirrors email's attached.files[0]);
      // any additional files are linked to the resulting deal afterward.
      if (coreFiles.length > 0) {
        const [sourceFile, ...restFiles] = coreFiles;
        const created = await createFileApi({
          orgId: selectedOrgId,
          fileName: sourceFile.name,
          sizeBytes: sourceFile.size,
          fileType: sourceFile.type || 'application/pdf',
          requestUploadUrl: true,
        });
        if (!created.uploadUrl?.url) {
          throw new Error('No presigned upload URL returned for the contract file');
        }
        await putFileToPresignedUrl(created.uploadUrl, sourceFile);

        const pushed = await pushDealThroughEngineApi({
          idempotencyKey: `upload-${created.file.fileId}`,
          sourceFileId: created.file.fileId,
          orgId: selectedOrgId,
          title,
          description: dealData.context || undefined,
        });

        // Link any additional files to the now-existing deal (no new deals).
        for (const extra of restFiles) {
          try {
            const extraCreated = await createFileApi({
              orgId: selectedOrgId,
              fileName: extra.name,
              sizeBytes: extra.size,
              fileType: extra.type || 'application/pdf',
              requestUploadUrl: true,
              dealIds: [pushed.dealId],
            });
            if (extraCreated.uploadUrl?.url) {
              await putFileToPresignedUrl(extraCreated.uploadUrl, extra);
            }
          } catch (linkErr) {
            // A failed supplemental file must not sink the primary deal.
            console.error('Failed to attach additional file:', linkErr);
          }
        }

        navigate(`${PATH_ROOT.main.deal}/${pushed.dealId}`);
        return;
      }

      // No-file path (template / meeting / empty) — create a bare deal as before.
      const stage = dealData.stage as DealStage | undefined;
      const result = await createDeal({
        orgId: selectedOrgId,
        title,
        description: dealData.context || undefined,
        initialStage: stage && Object.values(DealStage).includes(stage) ? stage : DealStage.drafting,
      });

      const dealId = result.deal.dealId;
      navigate(`${PATH_ROOT.main.deal}/${dealId}`);
    } catch (err) {
      console.error('Failed to create deal:', err);
      setIsCreating(false);
    }
  };

  const handleCancel = () => {
    if (isDemo && fromContractIq) {
      navigate(`${PATH_ROOT.demo.contractIq}?view=template-library`);
      return;
    }
    navigate(isDemo ? PATH_ROOT.demo.dealPipeline : PATH_ROOT.main.dealPipeline);
  };

  return (
    <DealCreationFlow
      onComplete={handleComplete}
      onCancel={handleCancel}
      initialMethod={initialMethod}
      initialTemplateId={initialTemplateId}
      onBackToTemplateLibrary={
        isDemo && fromContractIq
          ? () => navigate(`${PATH_ROOT.demo.contractIq}?view=template-library`)
          : undefined
      }
    />
  );
};

export default NewDealPage;
